const ROOT_PATH = "/";
const FILE_PATH_PATTERN = /\/[^/?#]+\.[^/?#]+$/;
const EXCLUDED_TRAILING_SLASH_PREFIXES = [
  "/admin",
  "/api",
  "/assets",
  "/images",
  "/storage",
  "/lovable-uploads",
  "/cdn",
];

export const STATIC_COMPATIBILITY_REDIRECTS = [
  {
    source_path: "/blog/how-to-write-personalized-holiday-elf-letters",
    target_path: "/blog/how-to-write-personalized-holiday-elf-letters-free-tool/",
    status_code: 301,
  },
  {
    source_path: "/article/how-to-write-personalized-holiday-elf-letters",
    target_path: "/blog/how-to-write-personalized-holiday-elf-letters-free-tool/",
    status_code: 301,
  },
  {
    source_path: "/blog/holiday-elf-ideas-planner",
    target_path: "/blog/creative-holiday-elf-arrival-ideas-2026/",
    status_code: 301,
  },
  {
    source_path: "/article/holiday-elf-ideas-planner",
    target_path: "/blog/creative-holiday-elf-arrival-ideas-2026/",
    status_code: 301,
  },
  {
    source_path: "/blog/holiday-elf-name-generator",
    target_path: "/elf-name-generator/",
    status_code: 301,
  },
  {
    source_path: "/article/holiday-elf-name-generator",
    target_path: "/elf-name-generator/",
    status_code: 301,
  },
];

function splitPathSuffix(path) {
  const match = path.match(/^([^?#]*)(.*)$/);
  return {
    pathname: match?.[1] || path,
    suffix: match?.[2] || "",
  };
}

export function stripTrailingSlash(pathname) {
  if (!pathname || pathname === ROOT_PATH) {
    return ROOT_PATH;
  }

  return pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
}

export function normalizeRedirectTarget(targetPath) {
  if (typeof targetPath !== "string" || !targetPath.startsWith("/")) {
    return targetPath;
  }

  const { pathname, suffix } = splitPathSuffix(targetPath);
  const normalizedPath = stripTrailingSlash(pathname);

  if (
    normalizedPath === ROOT_PATH ||
    FILE_PATH_PATTERN.test(normalizedPath) ||
    EXCLUDED_TRAILING_SLASH_PREFIXES.some((prefix) => normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`))
  ) {
    return `${normalizedPath}${suffix}`;
  }

  return `${normalizedPath}/${suffix}`;
}

export function normalizeRedirectRecord(redirect) {
  return {
    ...redirect,
    target_path: normalizeRedirectTarget(redirect.target_path),
  };
}

function createSourceVariants(sourcePath) {
  if (typeof sourcePath !== "string" || !sourcePath.startsWith("/")) {
    return [sourcePath];
  }

  const normalizedSource = stripTrailingSlash(sourcePath);

  if (
    normalizedSource === ROOT_PATH ||
    FILE_PATH_PATTERN.test(normalizedSource) ||
    EXCLUDED_TRAILING_SLASH_PREFIXES.some((prefix) => normalizedSource === prefix || normalizedSource.startsWith(`${prefix}/`))
  ) {
    return [normalizedSource];
  }

  return Array.from(new Set([normalizedSource, `${normalizedSource}/`]));
}

export function findStaticCompatibilityRedirect(sourcePath) {
  if (typeof sourcePath !== "string" || !sourcePath.startsWith("/")) {
    return null;
  }

  const normalizedSource = stripTrailingSlash(sourcePath) || ROOT_PATH;

  return (
    STATIC_COMPATIBILITY_REDIRECTS.find(
      (redirect) => stripTrailingSlash(redirect.source_path) === normalizedSource,
    ) ?? null
  );
}

export function buildRedirectsFileContent(redirects) {
  // Keep regenerated snapshots pointing at the final known destination. Older
  // article records can still target a dated blog slug that itself redirects.
  const targets = new Map();
  for (const record of [...STATIC_COMPATIBILITY_REDIRECTS, ...redirects]) {
    const source = stripTrailingSlash(record.source_path);
    if (!targets.has(source)) targets.set(source, normalizeRedirectRecord(record));
  }
  const resolveTarget = (targetPath) => {
    let target = normalizeRedirectTarget(targetPath);
    const visited = new Set();
    while (typeof target === "string" && target.startsWith("/") && !/[?#]/.test(target)) {
      const source = stripTrailingSlash(target);
      const next = targets.get(source);
      if (!next || ![301, 308].includes(Number(next.status_code))) break;
      if (visited.has(source)) throw new Error(`Redirect cycle at ${source}`);
      visited.add(source);
      target = next.target_path;
    }
    return target;
  };
  const overriddenSources = new Set(
    STATIC_COMPATIBILITY_REDIRECTS.map((redirect) => stripTrailingSlash(redirect.source_path)),
  );
  const seenSources = new Set(
    STATIC_COMPATIBILITY_REDIRECTS.flatMap((redirect) =>
      createSourceVariants(redirect.source_path),
    ),
  );

  const redirectLines = [];
  for (const redirect of redirects) {
    if (overriddenSources.has(stripTrailingSlash(redirect.source_path))) {
      continue;
    }

    const normalized = normalizeRedirectRecord(redirect);
    for (const sourcePath of createSourceVariants(normalized.source_path)) {
      if (seenSources.has(sourcePath)) {
        continue;
      }

      seenSources.add(sourcePath);
      redirectLines.push(
        `${sourcePath}  ${resolveTarget(normalized.target_path)}  ${normalized.status_code}`,
      );
    }
  }

  const compatibilityRedirectLines = STATIC_COMPATIBILITY_REDIRECTS.flatMap((redirect) => {
    const normalized = normalizeRedirectRecord(redirect);
    return createSourceVariants(normalized.source_path).map(
      (sourcePath) => `${sourcePath}  ${resolveTarget(normalized.target_path)}  ${normalized.status_code}`,
    );
  });

  return `# Legacy compatibility redirects
${compatibilityRedirectLines.join("\n")}

# SEO redirects from D1 snapshot
${redirectLines.join("\n")}

# Asset redirects for backwards compatibility
/lovable-uploads/* /images/:splat 301
/assets/lovable-uploads/* /images/:splat 301

# Legacy article path fallback
/article/* /blog/:splat/ 301

# Service Worker files - must be served with correct MIME type
/sw.js /sw.js 200
/sw.v2.js /sw.v2.js 200

# Legacy image redirects for backwards compatibility
/lovable-uploads/01a63850-954f-45ba-a1a5-721062991ffb.png  /logo.png  301
/lovable-uploads/e8e8f25e-9a4c-470d-98e9-425369ab8dad.png  /images/e8e8f25e-9a4c-470d-98e9-425369ab8dad.png  301
/lovable-uploads/6a2b9bae-ace7-423e-bd5f-afb56baf2b51.png  /images/6a2b9bae-ace7-423e-bd5f-afb56baf2b51.png  301
/lovable-uploads/309a9a62-cea9-417c-932b-2acc7d796f71.png  /images/309a9a62-cea9-417c-932b-2acc7d796f71.png  301

# Favicon files and root assets
/favicon.ico          /favicon.ico          200
/favicon.png          /favicon.png          200
/favicon-16x16.png    /favicon-16x16.png    200
/favicon-32x32.png    /favicon-32x32.png    200
/favicon-48x48.png    /favicon-48x48.png    200
/apple-touch-icon.png /apple-touch-icon.png 200
/android-chrome-192x192.png /android-chrome-192x192.png 200
/android-chrome-512x512.png /android-chrome-512x512.png 200
/logo.png             /logo.png             200
/elf-letter-template.png /elf-letter-template.png 200
/north-pole-elf-letter-template.png /north-pole-elf-letter-template.png 200
/manifest.json        /manifest.json        200
/robots.txt           /robots.txt           200
/sitemap.xml          /sitemap.xml          200
/sitemap.xml.gz       /sitemap.xml.gz       200

# Exclude assets from SPA routing
/assets/*   /assets/:splat   200
/images/*   /images/:splat   200
/*.js       /:splat          200
/*.css      /:splat          200
/*.svg      /:splat          200
/*.png      /:splat          200
/*.ico      /:splat          200
/*.woff2    /:splat          200
/*.woff     /:splat          200

# SPA fallback for the admin app only
/admin/*    /index.html    200
`;
}
